import { Link2, Ticket, Copy, Trophy } from "lucide-react";
import { toast } from "sonner";
import { TIER_ORDER, TIER_THRESHOLDS } from "@/lib/constants";
import { useLang } from "@/i18n/LanguageContext";

export function AffiliateBox({ creator }) {
  const { t } = useLang();
  const handle = creator.affiliate_handle;
  const code = creator.discount_code;
  const link = handle ? `${window.location.origin}/r/${handle}` : "";

  const copy = (val) => { navigator.clipboard.writeText(val); toast.success(t("cdash.copied")); };

  const ready = !!(handle && code);

  return (
    <div className="rounded-2xl border border-aura-border bg-aura-surface p-6 md:p-8" data-testid="affiliate-box">
      <div className="flex items-center gap-2 mb-2"><Link2 size={18} className="text-zinc-400" /><h2 className="font-display text-lg font-semibold text-white">{t("cdash.affiliateTitle")}</h2></div>
      <p className="text-sm text-zinc-500 mb-6 max-w-2xl">{t("cdash.affiliateDesc")}</p>

      {!ready ? (
        <p className="text-sm text-amber-300/80">{t("cdash.pendingApproval")}</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-xs uppercase tracking-wide text-zinc-500 mb-2">{t("cdash.yourLink")}</p>
            <div className="flex items-center gap-2 rounded-lg border border-aura-border bg-black/30 px-3 py-2.5">
              <span data-testid="affiliate-link" className="flex-1 truncate text-sm text-zinc-200">{link}</span>
              <button data-testid="copy-affiliate-link" onClick={() => copy(link)} className="shrink-0 inline-flex items-center gap-1.5 rounded-md border border-white/15 text-zinc-200 hover:bg-white/5 px-2.5 py-1.5 text-xs font-medium transition-colors"><Copy size={12} /> {t("cdash.copy")}</button>
            </div>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-zinc-500 mb-2">{t("cdash.yourCode")}</p>
            <div className="flex items-center gap-2 rounded-lg border border-aura-border bg-black/30 px-3 py-2.5">
              <Ticket size={15} className="text-emerald-400" />
              <span data-testid="affiliate-code" className="flex-1 font-mono text-sm text-white">{code}</span>
              <button data-testid="copy-affiliate-code" onClick={() => copy(code)} className="shrink-0 inline-flex items-center gap-1.5 rounded-md border border-white/15 text-zinc-200 hover:bg-white/5 px-2.5 py-1.5 text-xs font-medium transition-colors"><Copy size={12} /> {t("cdash.copy")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function LevelProgress({ creator }) {
  const { t } = useLang();
  const levels = t("tiers.levels") || [];
  const orders = creator.orders_count || 0;
  const curIdx = Math.max(TIER_ORDER.indexOf(creator.tier || "explorer"), 0);
  const nextTier = TIER_ORDER[curIdx + 1];
  const nextThreshold = nextTier ? TIER_THRESHOLDS[nextTier] : null;
  const remaining = nextThreshold != null ? Math.max(nextThreshold - orders, 0) : 0;
  const pct = nextThreshold ? Math.min((orders / nextThreshold) * 100, 100) : 100;

  return (
    <div className="rounded-2xl border border-aura-border bg-aura-surface p-6 md:p-8" data-testid="level-progress">
      <div className="flex items-center gap-2 mb-6"><Trophy size={18} className="text-amber-300" /><h2 className="font-display text-lg font-semibold text-white">{t("cdash.levelTitle")}</h2></div>

      <div className="flex items-center justify-between mb-3">
        {TIER_ORDER.map((tier, i) => (
          <div key={tier} className="flex flex-col items-center gap-1.5 flex-1">
            <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold border ${i <= curIdx ? "bg-white text-black border-white" : "bg-transparent text-zinc-500 border-zinc-700"}`}>{i + 1}</div>
            <span className={`text-xs ${i <= curIdx ? "text-white" : "text-zinc-600"}`}>{levels[i]?.name}</span>
          </div>
        ))}
      </div>

      <div className="h-2 rounded-full bg-zinc-800 overflow-hidden mt-4">
        <div className="h-full bg-gradient-to-r from-amber-400 to-emerald-400 transition-[width] duration-700" style={{ width: `${pct}%` }} />
      </div>

      <p className="mt-4 text-sm text-zinc-400" data-testid="level-progress-text">
        {nextTier
          ? t("cdash.toNext").replace("{n}", remaining).replace("{tier}", levels[curIdx + 1]?.name || nextTier)
          : t("cdash.maxLevel")}
      </p>
    </div>
  );
}
